import { Button, LoadingOverlay } from '@mantine/core';
import React, { useState } from 'react';
import { Arr } from '../../../CodesForFoundation';
import { useDraft2 } from '../../../CodesThatsGottaGoSomewhereElse';
import { JiraProjectData } from '../../../api/endpoints/jira';
import { SavingIndicator } from '../../../ui/SavingIndicator';
import { FlexScroller } from '../../../ui/Scrollers';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { CreateJiraIssueForm } from './CreateJiraIssueForm';
import {
    useCreateJiraIssueMutation,
    useIssueTypeState,
    useJiraIssueCreateSchemaQuery
} from './CreateJiraIssueHook';
import { CreateJiraIssueSource } from './CreateMultiJiraIssueView';
import { JiraIssueTypeInput } from './inputs/JiraIssueTypeInput';

export type CreateSingleJiraIssueView = {
    jiraProject: JiraProjectData,
    source?: CreateJiraIssueSource,
    onComplete: (info: any) => void,
};

export const CreateSingleJiraIssueView = ({ jiraProject, source, onComplete }: CreateSingleJiraIssueView) => {

    const [issueType, setIssueType] = useIssueTypeState(jiraProject);
    const schema = useJiraIssueCreateSchemaQuery(jiraProject.id, issueType.id);
    const create = useCreateJiraIssueMutation(jiraProject.id);

    const { draft, patchDraft } = useDraft2({
        summary: source?.summary ?? '',
        description: source?.description ?? '',
    } as Record<string, any>);

    const [error, setError] = useState<string | null>(null);
    const [errorFields, setErrorFields] = useState([] as string[]);

    const onChange = (id: string, val: any) => {
        patchDraft({ [id]: val });
    };

    const onCreate = () => {
        create.mutateAsync([{ ...source, ...draft, issueType }]).then(([result]) => {
            if (result.ok) {
                onComplete([result]);
            }
            else {
                setError(result.errors[0]);
                setErrorFields(
                    Arr.unique(result.errors.map(x => x.split(':')[0]))
                );
            }
        });
    };

    return <Flexer grow flex="column" gap="1" width="54">
        <Flexer relative grow flex="column" margin="0 -.75">
            <LoadingOverlay visible={schema.isFetching} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
            <FlexScroller shadow>
                <Flexer grow flex="column" gap="1" padding="0 .75">
                    <JiraIssueTypeInput
                        allowedValues={jiraProject.issueTypes}
                        value={issueType}
                        onChange={setIssueType}
                    />
                    <CreateJiraIssueForm
                        fields={schema.data}
                        values={draft}
                        errors={errorFields}
                        onChange={onChange}
                    />
                </Flexer>
            </FlexScroller>
        </Flexer>
        <Flexer flex="row" align="center" justify="space-between">
            <Flexer>
                {error && <SavingIndicator state="error" errorText={error} />}
            </Flexer>
            <Button variant="primary" loading={create.isLoading} onClick={onCreate}>
                Create Issue
            </Button>
        </Flexer>
    </Flexer>;
};
